<script setup>
import { useI18n } from 'vue-i18n';
import { createRealEstateBrokerDashboardPresenter } from '../composables/realEstateBrokerDashboardPresenter';

defineProps({
  loading: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['open']);

const { t } = useI18n();
const {
  money,
  statusLabel,
  statusClass,
  dateDetail,
  dateValue,
  address,
  addressDetail: detail,
  propertyCode,
  propertyImage,
  percentage,
  installmentLabel,
  transferProgressLabel,
} = createRealEstateBrokerDashboardPresenter(t);
</script>

<template>
  <div
    class="divide-y divide-lais-border-soft rounded-lais border border-lais-border-soft bg-white shadow-lais-card lg:hidden"
  >
    <p v-if="loading" class="p-6 text-center text-lais-text-muted">
      {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.LOADING') }}
    </p>
    <p v-else-if="!rows.length" class="p-6 text-center text-lais-text-muted">
      {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.EMPTY') }}
    </p>
    <article
      v-for="row in rows"
      :key="row.commission_split_id"
      class="cursor-pointer space-y-3 p-4 hover:bg-lais-bg-soft/60"
      tabindex="0"
      @click="emit('open', row.parcela_id)"
      @keydown.enter="emit('open', row.parcela_id)"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="font-semibold text-lais-text">{{ address(row) }}</p>
          <p v-if="detail(row)" class="truncate text-xs text-lais-text-muted">
            {{ detail(row) }}
          </p>
        </div>
        <span
          class="rounded-full px-2 py-1 text-xs font-semibold"
          :class="statusClass(row)"
        >
          {{ statusLabel(row) }}
        </span>
      </div>
      <dl class="grid grid-cols-2 gap-3 text-sm">
        <div v-if="!row.progresso_transferencia">
          <dt class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.VALUE') }}
          </dt>
          <dd class="font-bold text-lais-text">{{ money(row) }}</dd>
        </div>
        <div v-else class="col-span-2">
          <dt class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.VALUE') }}
          </dt>
          <dd class="font-bold text-lais-text">
            {{ transferProgressLabel(row) }}
          </dd>
          <progress
            class="mt-2 h-2 w-full accent-lais-primary"
            max="100"
            :value="row.progresso_transferencia.percentual"
          />
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PERCENTAGE') }}
          </dt>
          <dd>{{ percentage(row) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.INSTALLMENT') }}
          </dt>
          <dd>{{ installmentLabel(row) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.DATE') }}
          </dt>
          <dd>{{ dateDetail(row) }}</dd>
        </div>
      </dl>
      <div
        class="flex items-center gap-3 border-t border-lais-border-soft pt-3"
      >
        <img
          v-if="propertyImage(row)"
          :src="propertyImage(row)"
          :alt="t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_IMAGE')"
          class="size-10 rounded-full object-cover"
        />
        <span
          v-else
          class="i-lucide-image-off grid size-10 place-items-center rounded-full bg-lais-bg-soft text-lais-text-muted"
        />
        <div class="min-w-0">
          <p class="truncate text-xs font-semibold text-lais-text">
            {{
              t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_CODE_VALUE', {
                label: t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_CODE'),
                code: propertyCode(row),
              })
            }}
          </p>
          <p class="text-xs text-lais-text-muted">{{ dateValue(row) }}</p>
        </div>
      </div>
    </article>
  </div>
</template>
